import React, { useContext, useEffect } from 'react';
import { Box, Stack, Paper } from '@mui/material';
import { Table, TableBody, TableContainer, TableHead, TableRow, Button, Typography, IconButton } from '@mui/material';
import Grid from '@mui/material/Grid';
import { AccordionSummary, AccordionDetails, Accordion } from '../shared/components/accordion';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StyledTableRow from '../shared/components/styledTableRow';
import StyledTableCell from '../shared/components/styledTableCell';
import DocumentForm from '../shared/components/documentForm';
import ConfirmDialog from '../shared/components/confirmDialog';
import { FormMode } from '../utils/extensions/constantEx'
import { EmployeeFormContext } from '../utils/contexts/employeeFormContext';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { DownloadDocumentAPIUrl, EmployeeDocumentAPIUrl } from '../utils/extensions/functionEx';
import { HomeContext } from '../utils/contexts/homeContext';
import { useCookies } from 'react-cookie';

const EmployeeDocumentView = (props) => {
    const { formOpenMode, selectedEmployeeId, handlePageLoadingOpen, handlePageLoadingClose } = props;
    const { ShowAPIError, OpenErrorAlert, OpenSuccessAlert, homeState, ClearLoggedUser } = useContext(HomeContext);
    const { SetEmployeeDocuments, employeeDocs } = useContext(EmployeeFormContext);

    const [isOpenDocumentForm, setIsOpenDocumentForm] = React.useState(false);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = React.useState(false);
    const [selectedDocument, setSelectedDocument] = React.useState(null);

    const [cookies, setCookie] = useCookies(['hplus_home_care_user_session_code']);

    useEffect(() => {
        if ((formOpenMode === FormMode.ViewMode || formOpenMode === FormMode.EditMode) && selectedEmployeeId && selectedEmployeeId > 0) {
            getEmployeeDocsFromDB();
        }
    }, [formOpenMode, selectedEmployeeId]);

    const getEmployeeDocsFromDB = async () => {
        try {
            let tempDbDocJson = null;
            const tempEmpDocs = [];

            const headers = new Headers();
            headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);
            const response = await fetch(EmployeeDocumentAPIUrl(homeState.loggedUser.companyCode, selectedEmployeeId), { headers });
            if (response.status === 401) {
                OpenErrorAlert('User login history expired, please log in again.');
                setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                ClearLoggedUser();
            } else if (response.status === 200) {
                tempDbDocJson = await response.json();
            } else {
                ShowAPIError(response);
            }

            if (tempDbDocJson) {
                for (const tempDbDoc of tempDbDocJson) {
                    const tempEmployeeDoc = {
                        id: tempDbDoc.id,
                        document_id: tempDbDoc.document_id,
                        document_no: tempDbDoc.document_no,
                        document_type: tempDbDoc.document_type,
                        file_name: tempDbDoc.file_name,
                        content_type: tempDbDoc.content_type,
                        notes: tempDbDoc.notes
                    };
                    tempEmpDocs.push(tempEmployeeDoc);
                }
            }
            SetEmployeeDocuments([...tempEmpDocs]);
        }
        catch (error) {
            OpenErrorAlert(error);
        }
    }


    const handleAddClick = () => {
        setSelectedDocument(null);
        setIsOpenDocumentForm(true);
    }

    const handleSaveClick = async (docFormValue) => {
        setIsOpenDocumentForm(false);
        try {
            handlePageLoadingOpen();
            const tempDbDoc = {
                id: 0,
                document_id: docFormValue.uploadedFileId,
                document_type: docFormValue.documentType,
                document_no: docFormValue.docNo,
                notes: docFormValue.notes,
                employee_id: selectedEmployeeId
            };

            const headers = new Headers();
            headers.append("Accept", "application/json");
            headers.append("Accept-Encoding", "application/json");
            headers.append("Content-Type", "application/json");
            headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);
            const response = await fetch(EmployeeDocumentAPIUrl(homeState.loggedUser.companyCode), {
                method: "POST",
                headers: headers,
                body: JSON.stringify(tempDbDoc)
            });
            if (response.status === 401) {
                OpenErrorAlert('User login history expired, please log in again.');
                setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                ClearLoggedUser();
            } else if (response.status === 201) {
                OpenSuccessAlert('New employee document saved successfully.');
                await getEmployeeDocsFromDB();
            }
            ShowAPIError(response);
        }
        catch (error) {
            OpenErrorAlert(error)
        } finally {
            handlePageLoadingClose();
        }
    }

    const handleCancelClick = () => {
        setIsOpenDocumentForm(false);
        setSelectedDocument(null);
    }

    const handleDeleteClick = (docRowItem) => {
        setSelectedDocument(docRowItem);
        setIsDeleteConfirmOpen(true);
    }

    const handleDeleteOKClick = async () => {
        try {
            handlePageLoadingOpen();
            setIsDeleteConfirmOpen(false);
            const tempDbDoc = {
                id: selectedDocument.id,
                document_id: selectedDocument.document_id,
                document_type: '',
                document_no: '',
                notes: '',
                employee_id: selectedEmployeeId
            };

            const headers = new Headers();
            headers.append("Accept", "application/json");
            headers.append("Accept-Encoding", "application/json");
            headers.append("Content-Type", "application/json");
            headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);
            const response = await fetch(EmployeeDocumentAPIUrl(homeState.loggedUser.companyCode), {
                method: "DELETE",
                headers: headers,
                body: JSON.stringify(tempDbDoc)
            });
            if (response.status === 401) {
                OpenErrorAlert('User login history expired, please log in again.');
                setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                ClearLoggedUser();
            } else if (response.status === 200) {
                OpenSuccessAlert('employee document deleted successfully.');
                await getEmployeeDocsFromDB();
            }
            ShowAPIError(response);
        }
        catch (error) {
            OpenErrorAlert(error)
        } finally {
            setSelectedDocument(null);
            handlePageLoadingClose();
        }
    }

    const handleDeleteCancelClick = () => {
        setSelectedDocument(null);
        setIsDeleteConfirmOpen(false);
    }

    const handleDownloadClick = async (id) => {
        try {
            handlePageLoadingOpen();
            const headers = new Headers();
            headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);
            const response = await fetch(DownloadDocumentAPIUrl(homeState.loggedUser.companyCode, id), { headers });
            if (response.status === 401) {
                OpenErrorAlert('User login history expired, please log in again.');
                setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                ClearLoggedUser();
            } else if (response.status === 200) {
                const blob = await response.blob();
                const fileUrlBlob = URL.createObjectURL(blob);
                window.open(fileUrlBlob, '_blank');
            }
            else {
                ShowAPIError(response)
            }
        }
        catch (error) {
            OpenErrorAlert(error)
        } finally {
            handlePageLoadingClose();
        }
    }

    return (

        <Accordion>
            <AccordionSummary id="panel-employee-documents" expandIcon={<ExpandMoreIcon />}>
                <Typography>Employee Documents</Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Stack spacing={1}>
                    <Grid container spacing={1}>
                        <Grid size={12}>
                            <Box sx={{ pr: 1, float: 'right' }}>
                                <Stack direction="row" spacing={2}>
                                    {
                                        isDeleteConfirmOpen &&
                                        <ConfirmDialog isOpen={isDeleteConfirmOpen} title="Confirm Remove"
                                            message="Are you sure you want to remove the document?"
                                            handleClickOK={handleDeleteOKClick}
                                            handleClickCancel={handleDeleteCancelClick} />

                                    }
                                    {
                                        isOpenDocumentForm &&
                                        <DocumentForm isOpen={isOpenDocumentForm} isProfilePhoto={false} loggedUser={homeState.loggedUser}
                                            handleSaveClick={handleSaveClick} showAPIError={ShowAPIError} openErrorAlert={OpenErrorAlert}
                                            handleCancelClick={handleCancelClick} selectedDocument={selectedDocument} />
                                    }
                                    {
                                        formOpenMode === FormMode.ViewMode &&
                                        <Button variant="contained" onClick={handleAddClick}>Add</Button>
                                    }
                                </Stack>
                            </Box>
                        </Grid>
                        <Grid size={12}>
                            <TableContainer component={Paper} sx={{ maxHeight: '77vh' }}>
                                <Table stickyHeader sx={{ minWidth: 700 }}>
                                    <TableHead>
                                        <TableRow>
                                            <StyledTableCell sx={{ width: '250px' }}>Document</StyledTableCell>
                                            <StyledTableCell sx={{ width: '300px' }}>No</StyledTableCell>
                                            <StyledTableCell sx={{ width: '550px' }}>File Name</StyledTableCell>
                                            <StyledTableCell>Notes</StyledTableCell>
                                            <StyledTableCell sx={{ width: '100px' }}>&nbsp;</StyledTableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {employeeDocs.map((row) => (
                                            <StyledTableRow key={row.id.toString()}>
                                                <StyledTableCell component="th" scope="row">
                                                    {
                                                        !row.document_type ? '' :
                                                            (row.document_type === 'Aadhaar' ? 'Aadhaar' :
                                                                (row.document_type === 'DrivingLicence' ? 'Driving Licence' :
                                                                    (row.document_type === 'RationCard' ? 'Ration Card' :
                                                                        (row.document_type === 'VoterID' ? 'Voter ID' : row.document_type)
                                                                    )
                                                                )
                                                            )
                                                    }
                                                </StyledTableCell>
                                                <StyledTableCell>{row.document_no}</StyledTableCell>
                                                <StyledTableCell>{row.file_name}</StyledTableCell>
                                                <StyledTableCell>{row.notes}</StyledTableCell>
                                                <StyledTableCell>
                                                    {
                                                        formOpenMode === FormMode.ViewMode &&
                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                            <IconButton size="small" onClick={() => handleDownloadClick(row.document_id)}>
                                                                <FileDownloadIcon fontSize="inherit" />
                                                            </IconButton>
                                                            <IconButton size="small" color="error" onClick={() => handleDeleteClick(row)}>
                                                                <RemoveCircleOutlineIcon fontSize="small" />
                                                            </IconButton>
                                                        </Stack>
                                                    }
                                                </StyledTableCell>
                                            </StyledTableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                    </Grid>
                </Stack>
            </AccordionDetails>
        </Accordion>

    );
}
export default EmployeeDocumentView;